<template>
  <div :class="classList" class="c-questionnaire-question">
    <div v-if="warning" class="c-questionnaire-question__warning opacity:surface-medium hidden lg:block opacity-0" v-html="warning"/>
    <div class="c-questionnaire-question__title" v-html="title"/>
    <div
      v-if="descriptions"
      class="c-questionnaire-question__descriptions"
      v-html="descriptions"
    />
    <template v-if="answer">
      <div class="c-questionnaire-question__answer">
        <div
          v-for="item in propAnswer"
          :key="item.value"
          class="c-questionnaire-question__item"
        >
          <template v-if="item.type">
            <div
              class="c-questionnaire-question__text opacity:surface-medium"
              v-html="item.text"
            />
            <c-input
              v-bind="item.bind"
              :type="item.type"
              :name="item.value || item.name"
              adaptive="block"
              @on-input="onInput"
            />
          </template>
          <c-questionnaire-item v-else v-bind="item" @on-click="onClick"/>
        </div>
      </div>
      <div class="c-questionnaire-question__submit">
        <c-button
          :disabled="isDisabled"
          :text="translations.Further"
          class="c-questionnaire-question__button"
          size="large"
          @click="onSubmit"
        />
      </div>
      <div v-if="warning" class="c-questionnaire-question__warning opacity:surface-medium" v-html="warning"/>
    </template>
  </div>
</template>

<script>
import CButton from './CButton'
import CInput from './CInput'
import CQuestionnaireItem from './CQuestionnaireItem'
import { computed, ref, toRefs } from 'vue'
import Translation from 'dcode/classes/Translation'
import forEach from 'dcode/functions/forEach'
import isSelected from 'dcode/functions/isSelected'
import setValues from 'dcode/functions/setValues'
import useWatch from 'dcode/uses/useWatch'

export default {
  name: 'CQuestionnaireQuestion',
  components: {
    CInput,
    CButton,
    CQuestionnaireItem
  },
  emits: ['on-input'],
  props: {
    // Values
    title: String,
    descriptions: String,
    name: String,
    answer: Array,
    value: [Array, String],
    item: Object,

    // Options
    multiple: Boolean,
    max: Number
  },
  setup (props, context) {
    const {
      value
    } = toRefs(props)

    let only

    /**
     * @type {Object.<string, string>}
     */
    const translations = Translation.getByList([
      'Further'
    ])

    const propValue = useWatch(value, () => value.value, ['init'])

    const isOne = computed(() => props.answer?.length === 1)
    const isTree = computed(() => props.answer?.length === 3)
    const latest = ref(undefined)
    const warning = computed(() => {
      let warning

      if (latest.value) {
        const item = props.answer?.find(item => isSelected(item?.value, latest.value))
        warning = item?.warning
      } else {
        const item = props.answer?.find(item => isSelected(item?.value, propValue.value))
        warning = item?.warning
      }

      return warning instanceof Function ? warning(propValue.value) : warning
    })

    return {
      propAnswer: computed(() => {
        return forEach(props.answer, item => {
          return {
            type: item?.type,
            icon: item?.icon,
            name: props.name,
            text: item?.text,
            value: item?.value,
            only: item?.only,
            bind: item?.bind,
            selected: isSelected(item?.value, propValue.value),
            checkbox: props.multiple
          }
        })
      }),

      classList: computed(() => {
        return {
          'option-icon': props.answer?.findIndex(item => item?.icon) !== -1,
          'option-input': props.answer?.findIndex(item => item?.type) !== -1,
          'option-one': isOne.value,
          'option-tree': isTree.value
        }
      }),

      isDisabled: computed(() => {
        if (
          !Array.isArray(propValue.value) &&
          (propValue.value instanceof Object)
        ) {
          let check = true
          let length = 0

          forEach(propValue.value, item => {
            if (!(item && item.toString().trim() !== '')) {
              check = false
            }

            length++
          })

          return !(check && length === props.answer?.length)
        } else {
          return Array.isArray(propValue.value)
            ? propValue.value.length === 0
            : (propValue.value?.toString().trim() || '') === ''
        }
      }),
      warning,

      onClick (event) {
        if (event.only) {
          only = event.value
        } else if (Array.isArray(propValue.value) && only) {
          const key = propValue.value.indexOf(only)

          if (key >= 0) {
            propValue.value.splice(key, 1)
          }

          only = undefined
        }

        propValue.value = setValues(
          event.only
            ? (Array.isArray(propValue.value) && propValue.value.indexOf(event.value) === -1 ? undefined : propValue.value)
            : propValue.value,
          event.value,
          !!props.multiple,
          props.max,
          true
        )

        latest.value = undefined
      },
      onInput (event) {
        if (isOne.value) {
          propValue.value = event.validation ? event.value : ''
        } else {
          if (
            !(propValue.value) ||
            !(propValue.value instanceof Object) ||
            Array.isArray(propValue.value)
          ) {
            propValue.value = {}
          }

          propValue.value[event.name] = event.validation ? event.value : ''
        }

        latest.value = event.name
      },
      onSubmit () {
        context.emit('on-input', {
          item: { ...props.item },
          name: props.name,
          value: propValue.value
        })
      },

      translations
    }
  }
}
</script>

<style lang="scss">
@import "~dcode/styles/flex";
@import "~dcode/styles/font";
@import "~dcode/styles/margin";
@import "~dcode/styles/media";
@import "~dcode/styles/padding";

.c-questionnaire-question {
  --_qs-padding-top: var(--qs-padding-top, 72px);
  --_qs-padding-top-md: var(--qs-padding-top-md, 120px);
  --_qs-padding-top-ds: var(--qs-padding-top-ds, 0px);
  --_qs-width: var(--qs-width, 512px);
  --_qs-width-ex: var(--qs-width-ex, 960px);
  --_qs-ds-font-size: var(--qs-ds-font-size, 14px);
  --_qs-ds-font-size-md: var(--qs-ds-font-size-md, 16px);
  --_qs-ds-font-size-ds: var(--qs-ds-font-size-ds, 18px);
  --_qs-ds-font-height: var(--qs-ds-font-height, 17px);
  --_qs-ds-font-height-md: var(--qs-ds-font-height-md, 20px);
  --_qs-ds-font-height-ds: var(--qs-ds-font-height-ds, 24px);
  --_qs-tt-width: var(--qs-tt-width, auto);
  --_qs-tt-width-ds: var(--qs-tt-width-ds, 512px);
  --_qs-tt-width-ex: var(--qs-tt-width-ex, 900px);
  --_qs-an-padding: var(--qs-an-padding, 32px);
  --_qs-an-padding-ds: var(--qs-an-padding-ds, 40px);
  --_qs-wr-width: var(--qs-wr-width, auto);
  --_qs-wr-width-ds: var(--qs-wr-width-ds, 468px);
  --_qs-wr-width-ex: var(--qs-wr-width-ex, 600px);
  --_qs-wr-padding: var(--qs-wr-padding, 16px);
  --_qs-wr-padding-ds: var(--qs-wr-padding-ds, 32px);
  --_qs-it-padding: var(--qs-it-padding, 8px);
  --_qs-it-padding-ds: var(--qs-it-padding-ds, 12px);
  --_qs-it-width: var(--qs-it-width, 100%);
  --_qs-it-width-ds: var(--qs-it-width-ds, 288px);
  --_qs-it-width--icon-ds: var(--qs-it-width--icon-ds, 210px);
  --_qs-bt-width: var(--qs-bt-width, 100%);
  --_qs-bt-width-ds: var(--qs-bt-width-ds, 240px);

  --_qs-columns: var(--qs-columns, auto-fill);
  --_qs-size: var(--qs-size, 100%);
  --_qs-size-md: var(--qs-size-md, #{calc(100% / 2 - var(--_qs-it-padding))});

  padding-top: var(--_qs-padding-top);

  &.option-icon {
    --_qs-it-width-ds: var(--_qs-it-width--icon-ds);
  }

  &.option-one {
    --_qs-it-width-ds: 100%;
  }

  &__title {
    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
    text-align: center;
    margin: 0 auto;
    max-width: var(--_qs-tt-width);

    @include mediaMinWidth() {
      font-size: 32px;
      line-height: 36px;
    }
  }

  &__descriptions {
    @include font('subtitle2');
    padding-top: 16px;
    text-align: center;
    font-size: var(--_qs-ds-font-size);
    line-height: var(--_qs-ds-font-height);

    @include mediaMinWidth() {
      @include font('subtitle1');
    }
  }

  &__warning {
    padding-top: var(--_qs-wr-padding);
    text-align: center;
    margin: 0 auto;
    max-width: var(--_qs-wr-width);
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    @include mediaMinWidth() {
      --_qs-wr-padding: var(--_qs-wr-padding-ds);
    }
  }

  &__answer {
    @include paddingY(var(--_qs-an-padding));
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    gap: calc(var(--_qs-it-padding) * 2);
  }

  &__item {
    max-width: 100%;
    flex-basis: 100%;
    display: flex;
    flex-wrap: wrap;

    > * {
      flex-basis: 100%;
    }
  }

  &__text {
    padding-bottom: 12px;
  }

  &__submit {
    text-align: center;
  }

  &__button {
    width: var(--_qs-bt-width);
  }

  @include mediaMinWidth() {
    --_qs-padding-top: var(--_qs-padding-top-md);
    --_qs-an-padding: var(--_qs-an-padding-ds);
    --_qs-ds-font-size: var(--_qs-ds-font-size-md);
    --_qs-ds-font-height: var(--_qs-ds-font-height-md);
    --_qs-it-width: var(--_qs-it-width-ds);
    --_qs-bt-width: var(--_qs-bt-width-ds);

    --_qs-size: var(--_qs-size-md);

    &__item {
      flex-basis: calc(100% / 2 - var(--_qs-it-padding));
    }

    &__descriptions {
      line-height: 20px;
    }

    &.option-one &__item {
      flex-basis: 334px;
    }
  }

  @include mediaMinWidth(large) {
    --_qs-padding-top: var(--_qs-padding-top-ds);
    --_qs-it-padding: var(--_qs-it-padding-ds);
    --_qs-tt-width: var(--_qs-tt-width-ds);
    --_qs-wr-width: var(--_qs-wr-width-ds);
    --_qs-ds-font-size: var(--_qs-ds-font-size-ds);
    --_qs-ds-font-height: var(--_qs-ds-font-height-md);

    @include flexY;
    justify-content: center;
    height: 100%;

    &__answer {
      width: var(--_qs-width);
      margin: 0 auto;
    }

    &__item {
      flex-basis: 222px;
    }

    &.option-icon &__item {
      flex-basis: 170px;
    }

    &.option-one &__item {
      flex-basis: 468px;
    }

    &.option-tree,
    &.option-icon {
      --_qs-width: var(--_qs-width-ex);
    }
  }

  @include mediaMinWidth(extra) {
    --_qs-tt-width: var(--_qs-tt-width-ex);
    --_qs-wr-width: var(--_qs-wr-width-ex);

    &__answer {
      width: 640px;
    }

    &__item {
      flex-basis: 288px;
    }

    &.option-one &__item {
      flex-basis: 600px;
    }

    &.option-icon &__answer {
      width: 1280px;
      max-width: 100%;
    }

    &.option-icon &__item {
      flex-basis: 210px;
    }
  }
}
</style>
